import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import {
  Box,
  Heading,
  Stack,
  Text,
  TextLink,
  useResponsiveValue
} from '@machobear/design-system'

import mailIcon from '@/features/Company/icons/email-icon.png'
import globeIcon from '@/features/Company/icons/globe-icon.png'
import phoneIcon from '@/features/Company/icons/phone-icon.png'
import whiteGlobeIcon from '@/features/Company/icons/white-globe-icon.svg'
import whiteMailIcon from '@/features/Company/icons/white-mail-icon.svg'
import whitePhoneIcon from '@/features/Company/icons/white-phone-icon.svg'
import Head from '@/components/head'

import heroBanner from './hero.webp'

const PowerPropertiesProfile = ({ metadata }) => {
  const { t } = useTranslation('powerproperties')
  const profile = {
    banner: heroBanner.src,
    title: t('powerproperties:Power Properties'),
    url: '/company/powerproperties'
  }

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })
  const isDesktop = useResponsiveValue()({
    mobile: false,
    tablet: false,
    desktop: true
  })

  return (
    <>
      <Head
        title={`${profile.title} | liv.rent`}
        description={profile.pageDescription}
        path={profile.url}
        pageData={metadata}
        hideCustomerSupport
      />
      <Box
        style={{ height: isMobile ? '210px' : isDesktop ? '575px' : '375px' }}
        marginBottom="medium"
        overflow="hidden"
        position="relative"
      >
        <Box
          display="flex"
          alignItems="center"
          height="full"
          width="full"
          overflow="hidden"
        >
          <Image
            alt="Power Properties"
            fill
            priority
            src={heroBanner.src}
            style={{
              objectFit: 'cover',
              objectPosition: '80% 50%'
            }}
            sizes="(max-width: 768px) 768px, (max-width: 1200px) 1200px"
          />
          {isDesktop && (
            <Box display="flex" zIndex="dropdown" paddingLeft="gutter">
              <Box paddingLeft="xxxlarge" style={{ maxWidth: '45%' }}>
                <Stack space="xlarge">
                  <Text size="xlarge" weight="strong" tone="neutralInverted">
                    {t('powerproperties:heading')}
                  </Text>
                  <Text size="medium" weight="strong" tone="neutralInverted">
                    {t('powerproperties:subheading1')}
                  </Text>
                  <Text size="medium" tone="neutralInverted">
                    {t('powerproperties:subheading2')}
                  </Text>
                  <Text size="medium" tone="neutralInverted">
                    <Box
                      display="flex"
                      flexDirection="row"
                      gap="xxsmall"
                      alignItems="center"
                      style={{ width: 'max-content' }}
                    >
                      <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        gap="xxxsmall"
                      >
                        <Box>
                          <Image
                            src={whitePhoneIcon}
                            alt="White Phone Icon"
                            priority
                            width={16}
                            height={16}
                          />
                        </Box>
                        <Box
                          component="a"
                          href="tel:  403 509 0040"
                          cursor="pointer"
                        >
                          1 (403) 509-0040
                        </Box>
                      </Box>
                      <Box>|</Box>
                      <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        gap="xxxsmall"
                      >
                        <Box>
                          <Image
                            src={whiteMailIcon}
                            alt=""
                            priority
                            width={16}
                            height={16}
                          />
                        </Box>
                        <Box
                          component="a"
                          href="mailto:power@powerproperties.net"
                          target="_blank"
                          cursor="pointer"
                        >
                          power@powerproperties.net
                        </Box>
                      </Box>
                      <Box>|</Box>
                      <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        gap="xxxsmall"
                      >
                        <Box>
                          <Image
                            src={whiteGlobeIcon}
                            alt=""
                            priority
                            width={16}
                            height={16}
                          />
                        </Box>
                        <Box
                          component="a"
                          href="https://www.powerproperties.net/"
                          target="_blank"
                          cursor="pointer"
                        >
                          www.powerproperties.net
                        </Box>
                      </Box>
                    </Box>
                  </Text>
                </Stack>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      {!isDesktop && (
        <Box
          display="flex"
          justifyContent="center"
          width="full"
          marginY="large"
        >
          <Stack space="medium">
            <Box
              display="flex"
              alignItems="center"
              marginRight="large"
              marginX="medium"
            >
              <Heading level="3" weight="weak">
                {t('powerproperties:heading')}
              </Heading>
            </Box>
            <Box
              display="flex"
              flexDirection={{ mobile: 'column', tablet: 'row' }}
              gap={{ mobile: 'gutter', tablet: 'none' }}
              marginX="medium"
              marginBottom="xlarge"
              marginTop="medium"
              style={{
                maxWidth: '1096px'
              }}
            >
              <Box
                display="flex"
                flexDirection="column"
                marginRight="xxlarge"
                style={{
                  width: isMobile ? '100%' : 'fit-content'
                }}
              >
                <Stack space="medium">
                  <Stack space="gutter">
                    <Text size="standard" weight="strong">
                      {t('powerproperties:subheading1')}
                    </Text>
                    <Text size="standard">
                      {t('powerproperties:subheading2')}
                    </Text>
                  </Stack>
                </Stack>
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                background="neutralSoft"
                borderRadius="small"
                paddingY="small"
                paddingX="gutter"
                marginTop={{ mobile: 'gutter', tablet: 'none' }}
                style={{
                  height: 'min-content',
                  width: isMobile ? '100%' : 'fit-content'
                }}
              >
                <Stack space="medium">
                  <Box display="flex" alignItems="center" width="full">
                    <Box marginRight="xxsmall">
                      <Image
                        src={phoneIcon.src}
                        alt=""
                        priority
                        width={16}
                        height={16}
                      />
                    </Box>
                    <TextLink
                      href={`tel: 403 509 0040`}
                      rel="noreferrer noopener"
                    >
                      1 (403) 509-0040
                    </TextLink>
                  </Box>
                  <Box display="flex" alignItems="center" width="full">
                    <Box marginRight="xxsmall">
                      <Image
                        src={mailIcon.src}
                        alt=""
                        priority
                        width={16}
                        height={16}
                      />
                    </Box>
                    <TextLink
                      target="_blank"
                      href="mailto:power@powerproperties.net"
                      rel="noreferrer noopener"
                    >
                      power@powerproperties.net
                    </TextLink>
                  </Box>
                  <Box display="flex" alignItems="center" width="full">
                    <Box marginRight="xxsmall">
                      <Image
                        src={globeIcon.src}
                        alt=""
                        priority
                        width={16}
                        height={16}
                      />
                    </Box>
                    <TextLink
                      href="https://www.powerproperties.net/"
                      rel="noreferrer noopener"
                      target="_blank"
                    >
                      www.powerproperties.net
                    </TextLink>
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Stack>
        </Box>
      )}
      <Box
        textAlign="center"
        paddingBottom="gutter"
        paddingTop={{ mobile: 'none', tablet: 'gutter' }}
      >
        <Heading level="6">{t('powerproperties:Our Portfolio')}</Heading>
      </Box>
    </>
  )
}

export default PowerPropertiesProfile
